import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, MoreVert, Image } from '@mui/icons-material';
import { getBanners, deleteBanner } from '../../services/Api';
import { useNavigate } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedBanner, setMenuSelectedBanner] = useState(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  const navigate = useNavigate();

  const fetchBanners = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBanners();
      if (response.success) {
        setBanners(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch banners');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching banners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleMenuOpen = (event, banner) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedBanner(banner);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedBanner(null);
  };

  const handleDeleteClick = () => {
    setBannerToDelete(menuSelectedBanner.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!bannerToDelete) return;
    try {
      setLoading(true);
      const response = await deleteBanner(bannerToDelete);
      if (response.success) {
        setDeleteDialogOpen(false);
        setBannerToDelete(null);
        fetchBanners();
      } else {
        console.error('Failed to delete banner');
      }
    } catch (err) {
      console.error('An error occurred while deleting the banner:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedBanners = banners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
            Store Banners
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your promotional banners displayed on the mobile app
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton onClick={fetchBanners} disabled={loading} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/banners/modify')}
            sx={{ px: 4, backgroundColor: 'primary.main' }}
          >
            Create Banner
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="banners table">
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Locations</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : banners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <Image sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">
                      No banners found
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/banners/modify')}
                      sx={{ mt: 2 }}
                    >
                      Create your first banner
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedBanners.map((banner) => (
                  <TableRow key={banner.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={banner.image || 'https://via.placeholder.com/600x300?text=No+Image'}
                        sx={{ width: 100, height: 50, border: '1px solid', borderColor: 'text.border' }}
                      >
                        <Image />
                      </Avatar>
                    </TableCell>
                    <TableCell>{new Date(banner.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
                        {banner.locations && banner.locations.length > 0 ? (
                          banner.locations.map(loc => (
                            <Chip key={loc.id || loc._id} label={loc.name} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }} />
                          ))
                        ) : (
                          <Chip label="All Locations" size="small" variant="outlined" sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
                        <Chip label={banner.position} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banner.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={banner.isActive ? "success" : "default"}
                        variant={banner.isActive ? "filled" : "outlined"}
                        sx={{ fontWeight: 500, minWidth: 70 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, banner)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {banners.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={banners.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={1}
        PaperProps={{
          sx: {
            minWidth: 150,
            border: '1px solid',
            borderColor: 'text.border',
            mt: 1,
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/banners/modify/${menuSelectedBanner?.id}`, { state: { banner: menuSelectedBanner } });
          handleMenuClose();
        }}>
          <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <DeleteConfirm
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Banner?"
        message="Are you sure you want to delete this banner? This action will remove it from the mobile app for all users."
      />
    </Box>
  );
};

export default GetAllBanners;
